import type { HeadMeasure } from '@pz/scene';
import type { ProductDefinition } from './types.js';
import { HeadReveal, type HeadLanding } from './head-reveal.js';

/**
 * Background removal in the live preview.
 *
 * Until this existed, the cut-out happened only when the print file was made —
 * so a shopper uploaded a photo of their child on a sofa, saw the sofa on the
 * keychain, and had to take on trust that it would be gone. On a catalogue where
 * most products exist to carry a cut-out face, that is the single largest gap
 * between what the shopper approves and what they receive.
 *
 * The server writes its result under the key the print worker already reads, so
 * this is not a preview approximation: it is the same image, made early. What the
 * shopper accepts is literally what goes to the press.
 */

/** Mirrors the form's `PhotoState`, without importing the form into this file. */
export interface CutoutState {
  phase: 'cutting' | 'ready' | 'failed';
  url?: string;
  message?: string;
  /** The photo this is about: a late answer for a removed or replaced photo is ignored. */
  assetId?: string;
}

/** A face the server found, in fractions of the original photo. */
export interface DetectedFace {
  x: number;
  y: number;
  w: number;
  h: number;
  score: number;
}

/**
 * What the storefront may ask the cut-out service for.
 *
 * `enhance` is deliberately absent. It is a ×2 resample for the press — a
 * resolution step, not a look — and the preview draws at a fraction of the
 * print size anyway, so there is nothing for the shopper to see and no reason
 * to spend the upload budget on it. The print worker runs it on its own.
 */
export type CutoutRequestMode = 'background' | 'face' | 'blurBackground';

export interface CutoutApi {
  /** POST /v1/storefront/cutout. `amount` is the blur strength, for blurBackground. */
  cutout(
    assetId: string,
    mode?: CutoutRequestMode,
    amount?: number,
  ): Promise<{
    url: string | null;
    /** The copy the page draws the result with (the server's preview copy). */
    previewUrl?: string;
    engine: string;
    cached: boolean;
    faces?: DetectedFace[];
    /** Face cuts: each head in the cut-out, measured (see `cropForHead`). */
    heads?: HeadMeasure[];
    /** The shop's own sentence when its daily allowance for this is spent. */
    notice?: string;
  }>;
}

export interface CutoutOptions {
  api: CutoutApi;
  product: ProductDefinition;
  /** Re-point the asset id at the cut-out pixels and repaint. */
  onCut: (assetId: string, url: string) => void;
  /**
   * Progress for the upload card, which is where the shopper is often looking.
   * The overlay covers the preview; this covers everywhere else.
   */
  onState?: (optionId: string, state: CutoutState) => void;
  /**
   * More than one face was found. The caller asks which one — this class does
   * not, because the answer changes the shopper's ANSWER, not the picture.
   */
  onFaces?: (optionId: string, assetId: string, faces: DetectedFace[]) => void;
  /**
   * Exactly one face was found — no question to ask, so the caller may frame
   * the photo on it directly (Customily's "smart face placement"). A Mini Me
   * head slot fed a full-body photo should land on the head, not the shoes.
   */
  onAutoFrame?: (optionId: string, assetId: string, face: DetectedFace) => void;
  /**
   * A face cut came back with measured heads: place this one on the Mini Me
   * body (Customily's "smart face placement", fitted to the slot's visible
   * window). Resolves with where the picture now sits on screen, once the
   * preview has been re-rendered, so the animation can fly the head there.
   */
  onHeads?: (optionId: string, assetId: string, head: HeadMeasure, heads: HeadMeasure[]) => Promise<HeadLanding | undefined>;
  /** Several heads in one cut: the caller asks which, then places it. */
  onPickHead?: (optionId: string, assetId: string, heads: HeadMeasure[]) => void;
  /** The uploaded photo itself, shown being analysed while the cut runs. */
  originalUrl?: (assetId: string) => string | undefined;
  /**
   * How much of the face cut the shopper watches (shop setting
   * `cutoutAnimation`): the full analyse-cut-fly moment, the simple scanning
   * frame, or nothing over the preview.
   */
  animation?: () => 'full' | 'simple' | 'off';
  /** Where to show progress — the element containing the preview canvas. */
  stage?: () => HTMLElement | undefined;
  /** Copy, so the Serbian storefront and a future locale share one code path. */
  labels?: { working?: string; blurring?: string; face?: string; failed?: string };
}

/** An image element as the product definition ships it. */
type ImageLayer = Extract<ProductDefinition['sides'][number]['elements'][number], { type: 'image' }>;

/** The head the shopper most likely means: the one covering most of the photo. */
export function largest(heads: HeadMeasure[]): HeadMeasure {
  return heads.reduce((best, head) => (head.coverage > best.coverage ? head : best), heads[0]!);
}

const DEFAULT_LABELS = {
  working: 'Uklanjam pozadinu…',
  face: 'AI iseca glavu sa fotografije…',
  blurring: 'Zamućujem pozadinu…',
  failed: 'Pozadina nije uklonjena — slika ostaje kakva jeste.',
};

export class Cutout {
  /** Assets already handled, so re-entering a wizard step does not re-request. */
  private readonly done = new Map<string, string | null>();
  /** Faces per photo, so stepping back through the wizard does not re-ask. */
  private readonly faces = new Map<string, DetectedFace[]>();
  /** Measured heads per face-cut photo. */
  private readonly heads = new Map<string, HeadMeasure[]>();
  private reveal: HeadReveal | undefined;
  private readonly asked = new Set<string>();
  private readonly inFlight = new Map<string, Promise<void>>();
  private overlay: HTMLElement | undefined;

  constructor(private readonly options: CutoutOptions) {}

  /**
   * Whether any element driven by this option asks for its background removed.
   *
   * Read from the template rather than configured separately: the designer
   * already ticks "Remove background" on the layer in Studio, and asking them to
   * say it twice is how the two answers drift apart.
   */
  wants(optionId: string): boolean {
    return this.wantedMode(optionId) !== null;
  }

  /**
   * Which treatment the option's layers ask for, or null for none.
   *
   * A blurred background wins outright: it contains the cut (the subject is
   * segmented to stay sharp), so a layer that also says removeBackground or
   * faceCutout is one picture, not two. Below that, face beats a plain
   * background removal — the head is the stricter cut. The print worker
   * resolves both the same way, and the two must agree.
   */
  wantedMode(optionId: string): CutoutRequestMode | null {
    const elements = this.imageLayersOf(optionId);
    let mode: CutoutRequestMode | null = null;
    for (const element of elements) {
      if (element.effects?.some((e) => e.kind === 'blurBackground')) return 'blurBackground';
      if (element.effects?.some((e) => e.kind === 'faceCutout')) mode = 'face';
      else if (mode === null && element.effects?.some((e) => e.kind === 'removeBackground')) {
        mode = 'background';
      }
    }
    return mode;
  }

  /**
   * The blur strength the option's layer asks for, when it asks for a blurred
   * background. Sent along so the server keys the picture the way the print
   * worker will look for it; the server clamps and defaults it.
   */
  wantedAmount(optionId: string): number | undefined {
    for (const element of this.imageLayersOf(optionId)) {
      const blur = element.effects?.find((e) => e.kind === 'blurBackground');
      if (blur && 'amount' in blur && typeof blur.amount === 'number') return blur.amount;
    }
    return undefined;
  }

  /** The image layers this option drives, minus the AI-styled ones. */
  private imageLayersOf(optionId: string): ImageLayer[] {
    const elementIds = new Set(
      this.options.product.bindings
        .filter((b) => b.optionId === optionId)
        .map((b) => b.elementId),
    );
    if (elementIds.size === 0) return [];
    const layers: ImageLayer[] = [];
    for (const side of this.options.product.sides) {
      for (const element of side.elements) {
        if (!elementIds.has(element.id)) continue;
        if (element.type !== 'image') continue;
        // A layer that is also AI-styled belongs to the style flow alone; the
        // print worker makes the same choice, and the two must agree.
        if (element.effects?.some((e) => e.kind === 'aiStyle')) continue;
        layers.push(element);
      }
    }
    return layers;
  }

  /**
   * Cuts out a freshly uploaded photo.
   *
   * Never rejects: a cut-out that cannot be produced leaves the original photo
   * in place, which is a worse preview but a perfectly good sale. The print
   * worker will try again on its own later.
   */
  async run(optionId: string, assetId: string): Promise<void> {
    if (!this.wants(optionId)) return;

    const known = this.done.get(assetId);
    if (known !== undefined) {
      // Same photo as before — including the shopper stepping back and forth
      // through the wizard. Re-apply without asking the server again.
      if (known) {
        this.options.onCut(assetId, known);
        this.options.onState?.(optionId, { phase: 'ready', url: known, assetId });
      }
      this.askFaces(optionId, assetId);
      return;
    }
    const running = this.inFlight.get(assetId);
    if (running) return running;

    const task = this.perform(optionId, assetId).finally(() => this.inFlight.delete(assetId));
    this.inFlight.set(assetId, task);
    return task;
  }

  private async perform(optionId: string, assetId: string): Promise<void> {
    const mode = this.wantedMode(optionId) ?? 'background';
    const working =
      mode === 'blurBackground'
        ? (this.options.labels?.blurring ?? DEFAULT_LABELS.blurring)
        : mode === 'face'
          ? (this.options.labels?.face ?? DEFAULT_LABELS.face)
          : (this.options.labels?.working ?? DEFAULT_LABELS.working);
    const failed = this.options.labels?.failed ?? DEFAULT_LABELS.failed;
    const animation = this.options.animation?.() ?? 'full';
    // The full Mini Me moment needs a visible stage and someone to place the
    // head; otherwise the plain viewfinder (or nothing, when the shop said so).
    const stage = this.options.stage?.();
    const reveal =
      // Only connected, not necessarily laid out yet: on a compact product page
      // the design opens in a popup at the same moment the cut starts, and the
      // reveal sizes itself once the popup has its size.
      mode === 'face' && animation === 'full' && this.options.onHeads && stage?.isConnected
        ? new HeadReveal(stage, this.options.originalUrl?.(assetId))
        : undefined;
    this.reveal?.dispose();
    this.reveal = reveal;
    if (reveal) reveal.start();
    else if (animation !== 'off') this.show(working);
    this.options.onState?.(optionId, { phase: 'cutting', message: working, assetId });
    try {
      const answer = await this.options.api.cutout(assetId, mode, this.wantedAmount(optionId));
      // Drawn from the server's preview copy of the cut (a cut of a big photo
      // is as big as the photo); the original cut is what prints.
      const result = { ...answer, url: answer.previewUrl ?? answer.url };
      this.done.set(assetId, result.url);
      this.faces.set(assetId, result.faces ?? []);
      if (mode === 'face') this.heads.set(assetId, result.heads ?? []);
      if (result.url && reveal) {
        this.options.onCut(assetId, result.url);
        this.options.onState?.(optionId, { phase: 'ready', url: result.url, assetId });
        const heads = result.heads ?? [];
        if (heads.length === 1 && this.options.onHeads) {
          // One head: placed while the shopper watches it being cut out.
          this.asked.add(assetId);
          const head = heads[0]!;
          await reveal.reveal(result.url, [head.box], 0, () => this.options.onHeads!(optionId, assetId, head, heads));
          return;
        }
        // Several heads (the picker asks next) or none measured.
        await reveal.reveal(result.url, heads.map((h) => h.box), undefined);
        this.askFaces(optionId, assetId);
        return;
      }
      if (result.url) {
        this.options.onCut(assetId, result.url);
        this.options.onState?.(optionId, { phase: 'ready', url: result.url, assetId });
        this.hide();
      } else {
        // The shop's daily allowance being spent is not the same as the cut-out
        // having failed: it has an explanation the shopper can act on ("try
        // later"), so that sentence is shown instead of the generic one. Either
        // way the original photo stays and the design carries on.
        const message = result.notice ?? failed;
        this.options.onState?.(optionId, { phase: 'failed', message, assetId });
        if (reveal) reveal.fail(message);
        else this.hide(message);
      }
      // Asked after the picture has settled, and whether or not the cut-out
      // worked: which person the shopper meant is a separate question from
      // whether we managed to remove the background behind them.
      this.askFaces(optionId, assetId);
    } catch {
      // A network failure is not the shopper's problem to solve; their photo is
      // already uploaded and their order will still be right.
      this.done.set(assetId, null);
      this.options.onState?.(optionId, { phase: 'failed', message: failed, assetId });
      if (reveal) reveal.fail(failed);
      else this.hide(failed);
    }
  }

  /** The measured heads of a face-cut photo, when the server sent them. */
  headsFor(assetId: string): HeadMeasure[] {
    return this.heads.get(assetId) ?? [];
  }

  /** The cut-out made for this photo, if one was. */
  resultFor(assetId: string): string | undefined {
    return this.done.get(assetId) ?? undefined;
  }

  /**
   * Raises the "which face?" question, once per photo.
   *
   * One face is not a question, and asking again every time a shopper steps
   * back through the wizard would be nagging — so a photo is only ever put to
   * them once.
   */
  private askFaces(optionId: string, assetId: string): void {
    if (this.asked.has(assetId)) return;
    // Measured heads (a face cut) beat bare face boxes: placement knows the
    // head's real size, and a group's picker offers heads, not detections.
    const heads = this.heads.get(assetId) ?? [];
    if (heads.length > 0 && this.options.onHeads) {
      this.asked.add(assetId);
      if (heads.length === 1) void this.options.onHeads(optionId, assetId, heads[0]!, heads);
      else if (this.options.onPickHead) this.options.onPickHead(optionId, assetId, heads);
      else void this.options.onHeads(optionId, assetId, largest(heads), heads);
      return;
    }
    const faces = this.faces.get(assetId) ?? [];
    if (faces.length === 0) return;
    this.asked.add(assetId);
    if (faces.length === 1) {
      // One face is not a question — frame on it directly.
      this.options.onAutoFrame?.(optionId, assetId, faces[0]!);
      return;
    }
    this.options.onFaces?.(optionId, assetId, faces);
  }

  // Note: add-to-cart deliberately does NOT wait for this. The commit stores the
  // upload's id, and the print worker cuts out from that id whether or not the
  // preview got there first — so a shopper who adds to cart mid-cut still
  // receives a correct product.

  private show(text: string): void {
    const stage = this.options.stage?.();
    if (!stage) return;
    if (!this.overlay) {
      const overlay = document.createElement('div');
      overlay.className = 'pz-cutout';
      overlay.innerHTML =
        '<span class="pz-cutout-frame"><i></i><i></i><i></i><i></i></span>' +
        '<span class="pz-cutout-scan"></span>' +
        '<span class="pz-cutout-label"></span>';
      this.overlay = overlay;
    }
    const label = this.overlay.querySelector('.pz-cutout-label');
    if (label) label.textContent = text;
    this.overlay.classList.remove('is-done', 'is-error');
    if (this.overlay.parentElement !== stage) stage.appendChild(this.overlay);
  }

  private hide(message?: string): void {
    const overlay = this.overlay;
    if (!overlay) return;
    if (message) {
      const label = overlay.querySelector('.pz-cutout-label');
      if (label) label.textContent = message;
      // The sweep stops on failure — an animation still running while nothing
      // happens reads as a hang — but the overlay stays opaque long enough to
      // be read, and only then fades.
      overlay.classList.add('is-error');
      window.setTimeout(() => overlay.classList.add('is-done'), 2600);
      window.setTimeout(() => overlay.remove(), 3000);
      return;
    }
    overlay.classList.add('is-done');
    window.setTimeout(() => overlay.remove(), 300);
  }

  dispose(): void {
    this.overlay?.remove();
    this.overlay = undefined;
    this.reveal?.dispose();
    this.reveal = undefined;
  }
}
